import type { Refill } from '../../../stores/types';
import { formatDate, formatNumber } from '../../../utils/formatters';

export interface RefillItemProps {
  refill: Refill;
  onToggle: (refill: Refill) => void;
  onShowDetail: (rx: string) => void;
}

function renderCopay(refill: Refill): string {
  if (refill.unableToCalculate) return 'Unable to calculate';
  return `$${refill.copayAmt.toFixed(2)}`;
}

function renderStatus(raw: Record<string, unknown>): string {
  const status = String(raw.RefillStatus || '');
  return status.indexOf('After') === -1 ? status : `Refill ${status}`;
}

export function RefillItemRow({ refill, onToggle, onShowDetail }: RefillItemProps) {
  const raw = refill.raw;
  const nextfill = formatDate(raw.NextRefillDate as string);
  const status = renderStatus(raw);
  const writtenQty = formatNumber(Number(raw.WrittenQty || 0));
  const daysSupply = formatNumber(Number(raw.DaysSupply || 0));
  const trackingUrl = String(raw.MailTrackingUrl || '');

  return (
    <tr className="refill-table-row">
      <td style={{ textAlign: 'center', width: 126 }}>
        {refill.canSelect && (
          <input type="checkbox" checked={refill.selected} onChange={() => onToggle(refill)} />
        )}
      </td>
      <td style={{ textAlign: 'center', width: 122 }}>{refill.rx}</td>
      <td style={{ textAlign: 'center', width: 256 }}>
        <button
          type="button"
          className="drug-name"
          style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
          onClick={() => onShowDetail(refill.rx)}
        >
          {refill.name}
        </button>
      </td>
      <td style={{ textAlign: 'center', width: 206 }}>{writtenQty}</td>
      <td style={{ textAlign: 'center', width: 200 }}>{daysSupply}</td>
      <td style={{ textAlign: 'center', width: 220 }}>{nextfill}</td>
      <td style={{ textAlign: 'center', width: 150 }}>{formatNumber(refill.refillsLeft)}</td>
      <td
        style={{ textAlign: 'center', width: 200 }}
        className={`status-cell ${refill.refillClass}`}
      >
        <button
          type="button"
          className={`btn btn-sm ${refill.refillClass}`}
          onClick={() => onToggle(refill)}
        >
          {status}
          <br />
          {refill.trackingAvailable && trackingUrl && (
            <a
              className="track-pkg"
              href={trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
            >
              Track Package
            </a>
          )}
        </button>
      </td>
    </tr>
  );
}

export function RefillItemCard({ refill, onToggle, onShowDetail }: RefillItemProps) {
  const raw = refill.raw;
  const nextfill = formatDate(raw.NextRefillDate as string);
  const status = renderStatus(raw);
  const writtenQty = formatNumber(Number(raw.WrittenQty || 0));
  const daysSupply = formatNumber(Number(raw.DaysSupply || 0));

  return (
    <div className="col-md-3 grid-items" style={{ height: 180, padding: 5 }}>
      <table className="table-bordered container-refill">
        <tbody>
          <tr>
            <td>
              {refill.canSelect && (
                <input
                  type="checkbox"
                  checked={refill.selected}
                  onChange={() => onToggle(refill)}
                  style={{ float: 'left', marginLeft: 10 }}
                />
              )}
              <strong> Rx # {refill.rx}</strong>
            </td>
          </tr>
          <tr>
            <td id="name-cell">
              <button
                type="button"
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                onClick={() => onShowDetail(refill.rx)}
              >
                <strong>{refill.name}</strong>
              </button>
            </td>
          </tr>
          <tr>
            <td>Quantity:{writtenQty}</td>
          </tr>
          <tr>
            <td>Days Supply:{daysSupply}</td>
          </tr>
          <tr>
            <td>Next Fill: {nextfill}</td>
          </tr>
          <tr>
            <td>{formatNumber(refill.refillsLeft)} Refills Remaining</td>
          </tr>
          <tr>
            <td className="status-cell">
              <button
                type="button"
                title={String(raw.RefillMessage || '')}
                style={{ width: '100%' }}
                className={`btn btn-sm ${refill.refillClass}`}
                onClick={() => onToggle(refill)}
              >
                {status}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
